import { FC, useCallback, useEffect, useState } from 'react';
import classnames from 'classnames';
import { useUserIsLogged } from '@common/selectors/user.selectors';
import {
	useAppInitialized,
	useAppMobile,
} from '@common/selectors/app.selectors';
import { useRouterQueryTab } from '@common/selectors/router.selectors';
import { openModal } from '@modules/modals/store/modal.actions';
import { ModalIdType } from '@common/constants/modal/ids.modal.constants';
import { useAppDispatch } from '../../../hooks/store/useAppDispatch';
import { updateUrlQuery } from '../../store/app/app.actions';
import { trans } from '../../modules/translation/translate';
import { SettingsLayoutDesktop } from './SettingsLayout.desktop';
import { SettingsLayoutMobile } from './SettingsLayout.mobile';
import { SettingsLayoutContext } from './SettingsLayout.context';

export interface SettingsLayoutSection {
	id: string;
	name?: () => string;
	nameComponent?: any;
	notification?: string;
	isActive?: () => boolean;
	isLogged?: boolean;
	isNarrow?: boolean;
	onClick?: ({ el }: { el: SettingsLayoutSection }) => void;
	modal?: boolean;
	panelTitle?: () => string;
}

export interface SettingsLayoutProps {
	sections: SettingsLayoutSection[];
	defaultTab?: string;
	getSectionContent: any;
}

export const SettingsLayout: FC<SettingsLayoutProps> = ({
	sections,
	defaultTab,
	getSectionContent,
}) => {
	const [activeId, setActiveId] = useState('');
	const [activeTabData, setActiveTabData] =
		useState<SettingsLayoutSection>(null);

	const dispatch = useAppDispatch();

	const isLogged = useUserIsLogged();
	const isMobile = useAppMobile();
	const routerQueryTab = useRouterQueryTab();
	const isInitialized = useAppInitialized();

	const checkActiveTab_ = useCallback(() => {
		if (!sections.some((section) => section.id === routerQueryTab)) {
			dispatch(updateUrlQuery({ tab: defaultTab || sections?.[0]?.id }));
		}
	}, [defaultTab, dispatch, routerQueryTab, sections]);

	const getSectionContent_ = useCallback(
		(id: string, panelName: () => string, isNarrow?: boolean) => {
			const panelName_ = panelName ? (
				<h3 className="text-style-xl-medium">
					{trans({ label: panelName() })}
				</h3>
			) : null;
			const content = getSectionContent(id);
			if (!content) {
				return null;
			}
			return (
				<div
					key={id}
					className={classnames(`settings__panel settings__panel--${id}`, {
						'settings__panel--narrow-padding': isNarrow,
					})}
				>
					{panelName_}
					{content}
				</div>
			);
		},
		[getSectionContent],
	);

	const handleChangeSection_ = useCallback(
		(section: SettingsLayoutSection) => {
			if (section.onClick) {
				return section.onClick({ el: section });
			}
			if (section.modal) {
				dispatch(openModal(section.id as ModalIdType, 'SettingsLayout'));
				return;
			}
			dispatch(updateUrlQuery({ tab: section.id }));
			return;
		},
		[dispatch],
	);

	const getSectionData_ = useCallback(
		(id) => {
			// console.log('settingsLayout - getSectionData_', { sections, id });
			return sections.find((section) => section.id === id);
		},
		[sections],
	);

	const getSections_ = useCallback(() => {
		let activeId_ = '';
		let activeTabData_: SettingsLayoutSection = null;
		if (isInitialized) {
			activeTabData_ = getSectionData_(routerQueryTab);
			activeId_ = activeTabData_?.id;
			if (!activeTabData_ || (activeTabData_?.isLogged && !isLogged)) {
				activeTabData_ = getSectionData_(defaultTab || sections?.[0]?.id);
				activeId_ = activeTabData_?.id;
				updateUrlQuery({ tab: activeId_ });
			}
			// console.log('settingsLayout - getSections', {
			// 	activeTabData_,
			// 	activeTabData,
			// 	activeId_,
			// 	defaultTab,
			// 	routerQueryTab,
			// 	sections,
			// });
		}
		return {
			activeId: activeId_,
			activeTabData: activeTabData_,
		};
	}, [
		defaultTab,
		getSectionData_,
		isInitialized,
		isLogged,
		routerQueryTab,
		sections,
	]);

	useEffect(() => {
		checkActiveTab_();
	}, [checkActiveTab_, dispatch, isInitialized]);

	useEffect(() => {
		const { activeId: activeId_, activeTabData: activeTabData_ } =
			getSections_();
		if (activeId !== activeId_) {
			setActiveId(activeId_);
			setActiveTabData(activeTabData_);
		}
	}, [activeId, getSections_]);

	// console.log('SettingsLayout', { sections, activeId, activeTabData });

	return (
		<SettingsLayoutContext.Provider
			value={{
				sections,
				activeId,
				activeTabData,
				handleChangeSection: handleChangeSection_,
				getSectionContent: getSectionContent_,
			}}
		>
			{isMobile ? <SettingsLayoutMobile /> : <SettingsLayoutDesktop />}
		</SettingsLayoutContext.Provider>
	);
};
